import type { ReactNode } from 'react';
import { Modal as CommonModal } from '../common/Modal';

export interface V7ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Override the modal body width in pixels (defaults to 540). */
  width?: number;
}

/**
 * Modal — v7 ADAPTER over the existing common/Modal.
 *
 * Renames `isOpen` -> `open` to match the v7 prop convention and forwards the
 * pixel-based `width` prop to CommonModal's Tailwind `size` class so the
 * outer container is wide enough for the content (UAT 2026-05-13 — the prior
 * hardcoded `size="md"` capped the modal at 448px and clipped the Migration
 * Wizard's 3-column trio selector).
 *
 * Does NOT re-implement escape / scroll-lock / focus management — those stay
 * owned by common/Modal.tsx (single source of truth). Per RESEARCH.md lines
 * 838-862 + PATTERNS.md "Modal extension over duplication" — never duplicate
 * the keydown / scroll-lock effects here.
 */
type CommonSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl';

function pickSize(width: number): CommonSize {
  if (width <= 384) return 'sm';
  if (width <= 448) return 'md';
  if (width <= 512) return 'lg';
  if (width <= 576) return 'xl';
  if (width <= 672) return '2xl';
  return '4xl';
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 540,
}: V7ModalProps) {
  return (
    <CommonModal
      isOpen={open}
      onClose={onClose}
      title={title}
      size={pickSize(width)}
      footer={footer}
    >
      <div style={{ width: `${width}px` }}>{children}</div>
    </CommonModal>
  );
}
